import { supabase } from '../lib/supabase';

// Default notification preferences - matches database defaults
export const DEFAULT_NOTIFICATION_PREFERENCES = {
  email_enabled: true,
  push_enabled: true,

  // Email preferences (per type)
  email_round_invitation: true,
  email_round_response: true,
  email_round_reminder: true,
  email_round_cancelled: true,
  email_weather_alert: true,
  email_availability_request: true,
  email_group_invite: true,
  email_group_member_joined: false,
  email_group_member_left: false,
  email_group_update: false,

  // Push preferences (per type)
  push_round_invitation: true,
  push_round_response: true,
  push_round_reminder: true,
  push_round_cancelled: true,
  push_weather_alert: true,
  push_availability_request: true,
  push_group_invite: true,
  push_group_member_joined: false,
  push_group_member_left: false,
  push_group_update: false,
};

// Helper function to get current user
const getCurrentUser = async (providedUser = null) => {
  if (providedUser) {
    return providedUser;
  }
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

/**
 * Get user's notification preferences
 * Returns preferences with defaults applied for missing values
 */
export const getNotificationPreferences = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in getNotificationPreferences');
      return { data: { ...DEFAULT_NOTIFICATION_PREFERENCES }, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No row found - return defaults (this is expected for new users)
        return { data: { ...DEFAULT_NOTIFICATION_PREFERENCES, user_id: user.id }, error: null };
      }
      // Handle 406 errors (RLS policy)
      if (error.status === 406) {
        return { data: { ...DEFAULT_NOTIFICATION_PREFERENCES, user_id: user.id }, error: null };
      }
      console.error('Error fetching notification preferences:', error);
      return { data: { ...DEFAULT_NOTIFICATION_PREFERENCES }, error };
    }

    // Merge with defaults to ensure all keys exist
    return {
      data: { ...DEFAULT_NOTIFICATION_PREFERENCES, ...data },
      error: null
    };
  } catch (error) {
    console.error('Exception in getNotificationPreferences:', error);
    return { data: { ...DEFAULT_NOTIFICATION_PREFERENCES }, error };
  }
};

/**
 * Update a single notification preference
 * Uses upsert to create row if it doesn't exist
 */
export const updateNotificationPreference = async (key, value, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in updateNotificationPreference');
      return { data: null, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: user.id,
        [key]: value,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      })
      .select()
      .single();

    if (error) {
      console.error('Error updating notification preference:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in updateNotificationPreference:', error);
    return { data: null, error };
  }
};

/**
 * Update multiple notification preferences at once
 * Useful for bulk updates from settings page
 */
export const updateNotificationPreferences = async (preferences, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in updateNotificationPreferences');
      return { data: null, error: 'User not authenticated' };
    }

    // Filter out any non-preference fields that might have been passed
    const validKeys = Object.keys(DEFAULT_NOTIFICATION_PREFERENCES);
    const filteredPreferences = {};
    for (const key of validKeys) {
      if (key in preferences) {
        filteredPreferences[key] = preferences[key];
      }
    }

    const { data, error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: user.id,
        ...filteredPreferences,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      })
      .select()
      .single();

    if (error) {
      console.error('Error updating notification preferences:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception in updateNotificationPreferences:', error);
    return { data: null, error };
  }
};

/**
 * Toggle a master channel (email or push) on/off
 * Convenience function for master toggle switches
 */
export const toggleChannelEnabled = async (channel, enabled, currentUser = null) => {
  const key = channel === 'email' ? 'email_enabled' : 'push_enabled';
  return await updateNotificationPreference(key, enabled, currentUser);
};

/**
 * Toggle a specific notification type for a channel
 * e.g., toggleNotificationType('round_invitation', 'email', false)
 */
export const toggleNotificationType = async (type, channel, enabled, currentUser = null) => {
  const key = `${channel}_${type}`;
  return await updateNotificationPreference(key, enabled, currentUser);
};

/**
 * Reset all preferences to defaults
 * Deletes the row so defaults apply
 */
export const resetNotificationPreferences = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('User not authenticated in resetNotificationPreferences');
      return { data: null, error: 'User not authenticated' };
    }

    const { error } = await supabase
      .from('notification_preferences')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      console.error('Error resetting notification preferences:', error);
      return { data: null, error };
    }

    return { data: { ...DEFAULT_NOTIFICATION_PREFERENCES }, error: null };
  } catch (error) {
    console.error('Exception in resetNotificationPreferences:', error);
    return { data: null, error };
  }
};

/**
 * Check if a specific notification should be sent for a user
 * Uses the RPC function for server-side checking
 * Note: This is mainly used by edge functions, not client code
 */
export const shouldSendNotification = async (userId, notificationType, channel) => {
  try {
    const { data, error } = await supabase.rpc('should_send_notification', {
      p_user_id: userId,
      p_notification_type: notificationType,
      p_channel: channel
    });

    if (error) {
      console.error('Error checking notification preference:', error);
      // Default to true on error to avoid missing notifications
      return true;
    }

    return data;
  } catch (error) {
    console.error('Exception in shouldSendNotification:', error);
    return true;
  }
};
